import React, { useEffect, useMemo, useRef, useState } from 'react';
import { portalApi, resolveFileUrl } from '../../../lib/portalApi';
import { useAuth, isMentorUser } from '../../../context/AuthContext';
import {
  Search,
  Filter,
  FileText,
  FileCode,
  Image as ImageIcon,
  ExternalLink,
  Download,
  X,
  FolderOpen,
  Calendar,
  Layers,
  CheckCircle2,
  Upload,
  AlertCircle,
  Trash2,
  Loader2,
  FilePlus,
  RefreshCw,
  ShieldCheck,
  Eye,
  EyeOff,
  PlusCircle,
  CheckSquare,
} from 'lucide-react';

const SOURCE_OPTIONS = [
  { id: 'all', label: 'All Sources' },
  { id: 'shared_startup', label: 'Visible to Startup' },
  { id: 'admin_only', label: 'Admin Only' },
  { id: 'application', label: 'Application' },
  { id: 'company_profile', label: 'Company Profile' },
  { id: 'due_diligence', label: 'Due Diligence' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'batch_task', label: 'Batch Tasks' },
  { id: 'vault', label: 'Central Vault' },
];

const PRESET_REQUEST_DOCS = [
  'Pitch Deck (Latest Version)',
  'Cap Table & Shareholding Structure',
  'Certificate of Incorporation',
  'Founder KYC & PAN/Aadhaar',
  'Financial Model / Projections',
  'Accelerator Cohort Agreement',
  'GST / Tax Registration Certificate',
  'IP & Trademark Filings',
];

const ALLOWED_EXTS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'csv',
  'png',
  'jpg',
  'jpeg',
  'webp',
  'gif',
  'zip',
  'tar',
  'gz',
  'txt',
];

const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024; // 15MB

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function getFileIcon(url = '', name = '') {
  const combined = `${url} ${name}`.toLowerCase();
  if (combined.match(/\.(png|jpe?g|webp|gif|svg)/)) {
    return <ImageIcon className="w-4 h-4 text-pink-400" />;
  }
  if (combined.match(/\.(pdf)/)) {
    return <FileText className="w-4 h-4 text-red-400" />;
  }
  if (combined.match(/\.(zip|tar|gz|json|csv|xlsx?|docx?)/)) {
    return <FileCode className="w-4 h-4 text-emerald-400" />;
  }
  return <FileText className="w-4 h-4 text-[#ff8ec5]" />;
}

function normalizeSourceKey(source = '') {
  const s = String(source).toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (s.includes('application')) return 'application';
  if (s.includes('profile')) return 'company_profile';
  if (s.includes('diligence')) return 'due_diligence';
  if (s.includes('onboarding')) return 'onboarding';
  if (s.includes('task') || s.includes('deliverable') || s.includes('batch')) return 'batch_task';
  if (s.includes('vault')) return 'vault';
  return s;
}

function sourceBadge(source) {
  const key = normalizeSourceKey(source);
  switch (key) {
    case 'application':
      return (
        <span className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/20 text-[10px]">
          Application
        </span>
      );
    case 'company_profile':
      return (
        <span className="px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 text-[10px]">
          Profile
        </span>
      );
    case 'due_diligence':
      return (
        <span className="px-2 py-0.5 rounded-full bg-pink-500/10 text-pink-300 border border-pink-500/20 text-[10px]">
          Due Diligence
        </span>
      );
    case 'onboarding':
      return (
        <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20 text-[10px]">
          Onboarding
        </span>
      );
    case 'batch_task':
      return (
        <span className="px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-300 border border-purple-500/20 text-[10px]">
          Batch Task
        </span>
      );
    case 'vault':
      return (
        <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 text-[10px]">
          Central Vault
        </span>
      );
    default:
      return (
        <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-[10px]">
          {source}
        </span>
      );
  }
}

export function SearchableStartupDocuments({
  startupId,
  applicationId,
  initialDocuments = null,
  onClose,
  title = 'Startup Documents Repository',
}) {
  const { user } = useAuth();
  const isMentor = isMentorUser(user);
  const fileInputRef = useRef(null);

  const [documents, setDocuments] = useState(initialDocuments || []);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(!initialDocuments);
  const [error, setError] = useState('');

  // Upload UI state
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]); // array of { file, id, status: 'queued'|'uploading'|'success'|'error', error?: string }
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recentUploadNotice, setRecentUploadNotice] = useState(null); // { count: number, hiddenByFilters: boolean }

  // Startup Access & Request state
  const [togglingDocId, setTogglingDocId] = useState(null);
  const [showRequestPanel, setShowRequestPanel] = useState(false);
  const [requestTitle, setRequestTitle] = useState('');
  const [requestCategory, setRequestCategory] = useState('Legal & Compliance');
  const [requestRequired, setRequestRequired] = useState(true);
  const [requesting, setRequesting] = useState(false);

  const fetchDocs = () => {
    if (initialDocuments && !startupId && !applicationId) {
      setDocuments(initialDocuments);
      return;
    }
    setLoading(true);
    setError('');

    const promise = startupId
      ? portalApi.getStartupDocuments(startupId, {
          source: sourceFilter !== 'all' ? sourceFilter : undefined,
          q: searchQuery.trim() || undefined,
        })
      : applicationId
        ? portalApi.getApplicationDocuments(applicationId, {
            q: searchQuery.trim() || undefined,
          })
        : Promise.resolve({ documents: [] });

    promise
      .then((res) => {
        setDocuments(res.documents || []);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load documents');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchDocs();
  }, [startupId, applicationId, sourceFilter]);

  // Client-side search filtering on top of server query
  const filteredDocs = useMemo(() => {
    let list = documents;
    if (sourceFilter === 'shared_startup') {
      list = list.filter((d) => Boolean(d.sharedWithStartup));
    } else if (sourceFilter === 'admin_only') {
      list = list.filter((d) => !d.sharedWithStartup);
    } else if (sourceFilter !== 'all') {
      list = list.filter((d) => {
        const normDocSource = normalizeSourceKey(d.source || d.sourceLabel);
        return normDocSource === sourceFilter;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (d) =>
          d.label?.toLowerCase().includes(q) ||
          d.name?.toLowerCase().includes(q) ||
          d.title?.toLowerCase().includes(q) ||
          d.originalName?.toLowerCase().includes(q) ||
          d.source?.toLowerCase().includes(q) ||
          d.sourceLabel?.toLowerCase().includes(q) ||
          d.stageTitle?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [documents, sourceFilter, searchQuery]);

  // Toggle individual document startup access
  const handleToggleAccess = async (doc, grant) => {
    if (!startupId || !doc.docId) {
      setError('Cannot modify access: missing document database ID');
      return;
    }
    try {
      setTogglingDocId(doc.id || doc.docId);
      setError('');
      await portalApi.toggleStartupDocumentAccess(startupId, doc.docId, grant);
      setDocuments((prev) =>
        prev.map((d) =>
          d.docId === doc.docId || d.id === doc.id
            ? { ...d, sharedWithStartup: grant }
            : d
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to update startup access');
    } finally {
      setTogglingDocId(null);
    }
  };

  // Bulk grant or revoke access
  const handleBulkAccess = async (grant) => {
    if (!startupId) return;
    const targetDocIds = filteredDocs.filter((d) => d.docId).map((d) => d.docId);
    if (targetDocIds.length === 0) {
      setError('No vault documents found to update access in current view');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await portalApi.bulkStartupDocumentAccess(startupId, targetDocIds, grant);
      fetchDocs();
    } catch (err) {
      setError(err.message || 'Failed to bulk update access');
      setLoading(false);
    }
  };

  // Request a document requirement from the startup
  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!requestTitle.trim() || !startupId) return;
    try {
      setRequesting(true);
      setError('');
      await portalApi.requestStartupDocument(startupId, {
        heading: requestTitle.trim(),
        category: requestCategory,
        isRequired: requestRequired,
      });
      setRequestTitle('');
      setShowRequestPanel(false);
      fetchDocs();
    } catch (err) {
      setError(err.message || 'Failed to request document');
    } finally {
      setRequesting(false);
    }
  };

  // Handle selected files for upload
  const handleFilesAdded = (filesList) => {
    if (!filesList || filesList.length === 0) return;
    const newItems = Array.from(filesList).map((file) => {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      let validationError = null;
      if (!ALLOWED_EXTS.includes(ext)) {
        validationError = `Unsupported format (.${ext}). Supported: PDF, DOC, XLS, CSV, images, ZIP.`;
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        validationError = `File exceeds 15MB limit (${formatBytes(file.size)}).`;
      }
      return {
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        status: validationError ? 'error' : 'queued',
        error: validationError,
      };
    });
    setUploadQueue((prev) => [...prev, ...newItems]);
    setShowUploadPanel(true);
  };

  const removeQueueItem = (id) => {
    setUploadQueue((prev) => prev.filter((item) => item.id !== id));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) {
      handleFilesAdded(e.dataTransfer.files);
    }
  };

  const handleUploadSubmit = async () => {
    const pendingItems = uploadQueue.filter(
      (item) => item.status === 'queued' || (item.status === 'error' && !item.error?.includes('limit'))
    );
    if (!pendingItems.length) return;

    setUploading(true);
    setRecentUploadNotice(null);

    // Mark pending items as uploading
    setUploadQueue((prev) =>
      prev.map((item) =>
        pendingItems.some((p) => p.id === item.id) ? { ...item, status: 'uploading', error: null } : item
      )
    );

    const formData = new FormData();
    pendingItems.forEach((item) => {
      formData.append('files', item.file);
    });

    try {
      const res = startupId
        ? await portalApi.uploadStartupDocuments(startupId, formData)
        : applicationId
          ? await portalApi.uploadApplicationDocuments(applicationId, formData)
          : null;

      if (!res) throw new Error('No entity ID available for upload');

      // Update queue statuses
      setUploadQueue((prev) =>
        prev.map((item) => {
          if (pendingItems.some((p) => p.id === item.id)) {
            return { ...item, status: 'success' };
          }
          return item;
        })
      );

      // Check if newly uploaded documents are hidden by active filters
      const hiddenByFilter = sourceFilter !== 'all' || Boolean(searchQuery.trim());
      setRecentUploadNotice({
        count: pendingItems.length,
        hiddenByFilters: hiddenByFilter,
      });

      // Refetch document list
      fetchDocs();

      // Automatically clear successfully uploaded items after short delay
      setTimeout(() => {
        setUploadQueue((prev) => prev.filter((item) => item.status !== 'success'));
      }, 4000);
    } catch (err) {
      setUploadQueue((prev) =>
        prev.map((item) => {
          if (pendingItems.some((p) => p.id === item.id)) {
            return { ...item, status: 'error', error: err.message || 'Upload failed' };
          }
          return item;
        })
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-[#ff8ec5]" />
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-white/10 text-white/70 font-mono">
            {filteredDocs.length} file{filteredDocs.length === 1 ? '' : 's'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {!isMentor && (
            <>
              <button
                type="button"
                onClick={() => {
                  setShowRequestPanel((prev) => !prev);
                  setShowUploadPanel(false);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all ${
                  showRequestPanel
                    ? 'border-[#D42B92] bg-[#321129] text-[#FF8EC5]'
                    : 'border-[#2C2C34] bg-[#08080A] text-[#8E8E99] hover:text-[#F2F2F4] hover:border-white/20'
                }`}
              >
                <PlusCircle className="w-3.5 h-3.5 text-[#D42B92]" />
                <span>{showRequestPanel ? 'Close Request' : 'Request Doc'}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowUploadPanel((prev) => !prev);
                  setShowRequestPanel(false);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#b02279] hover:from-[#e0349f] hover:to-[#be2783] text-white text-xs font-medium transition-all shadow-sm shadow-[#D42B92]/20"
              >
                <Upload className="w-3.5 h-3.5" />
                <span>{showUploadPanel ? 'Close Upload' : 'Upload Documents'}</span>
              </button>
            </>
          )}

          <button
            type="button"
            onClick={fetchDocs}
            title="Refresh repository"
            disabled={loading}
            className="p-1.5 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Request Document Drawer */}
      {showRequestPanel && !isMentor && (
        <form onSubmit={handleRequestSubmit} className="rounded-2xl border border-[#D42B92]/30 bg-[#131318] p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-white">
              <PlusCircle className="w-4 h-4 text-[#D42B92]" />
              <span>Request New Document from Startup</span>
            </div>
            <button
              type="button"
              onClick={() => setShowRequestPanel(false)}
              className="p-1 rounded text-white/40 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div>
            <label className="block text-xs text-[#8E8E99] mb-1.5 font-medium">Quick Presets</label>
            <div className="flex flex-wrap gap-1.5">
              {PRESET_REQUEST_DOCS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setRequestTitle(preset)}
                  className={`px-2.5 py-1 rounded-lg text-xs transition-all border ${
                    requestTitle === preset
                      ? 'border-[#D42B92] bg-[#321129] text-[#FF8EC5] font-semibold'
                      : 'border-[#2C2C34] bg-[#08080A] text-[#8E8E99] hover:text-[#F2F2F4]'
                  }`}
                >
                  {preset}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#8E8E99] mb-1 font-medium">Document Title *</label>
              <input
                type="text"
                required
                value={requestTitle}
                onChange={(e) => setRequestTitle(e.target.value)}
                placeholder="e.g. Cap Table (Signed)"
                className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3 py-2 text-sm text-[#F2F2F4] focus:outline-none focus:border-[#D42B92] placeholder-[#8E8E99]/50"
              />
            </div>
            <div>
              <label className="block text-xs text-[#8E8E99] mb-1 font-medium">Category</label>
              <select
                value={requestCategory}
                onChange={(e) => setRequestCategory(e.target.value)}
                className="w-full bg-[#08080A] border border-[#2C2C34] rounded-xl px-3 py-2 text-sm text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
              >
                <option value="Legal & Compliance">Legal & Compliance</option>
                <option value="Fundraising">Fundraising</option>
                <option value="Finance">Finance</option>
                <option value="Compliance">Compliance</option>
                <option value="IP & Legal">IP & Legal</option>
                <option value="General">General</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <label className="flex items-center gap-2 text-xs text-[#8E8E99] cursor-pointer">
              <input
                type="checkbox"
                checked={requestRequired}
                onChange={(e) => setRequestRequired(e.target.checked)}
                className="rounded accent-[#D42B92]"
              />
              <span>Mandatory requirement</span>
            </label>

            <button
              type="submit"
              disabled={requesting || !requestTitle.trim()}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#D42B92] to-[#b02279] hover:from-[#e0349f] hover:to-[#be2783] text-white text-xs font-semibold flex items-center gap-1.5 transition-all shadow-sm shadow-[#D42B92]/20 disabled:opacity-40"
            >
              <PlusCircle className="w-3.5 h-3.5" />
              <span>{requesting ? 'Creating Request...' : 'Send Request to Startup'}</span>
            </button>
          </div>
        </form>
      )}

      {/* Upload Drawer / Dropzone Panel */}
      {showUploadPanel && !isMentor && (
        <div className="rounded-2xl border border-[#d42b92]/30 bg-black/40 p-4 space-y-4 transition-all">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-white">
              <FilePlus className="w-4 h-4 text-[#ff8ec5]" />
              <span>Upload New Documents</span>
            </div>
            <span className="text-[11px] text-white/40">Max 15MB per file · PDF, DOC, XLS, CSV, images</span>
          </div>

          {/* Drag & Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
              isDragging
                ? 'border-[#ff8ec5] bg-[#d42b92]/20'
                : 'border-white/20 bg-white/[0.015] hover:border-[#d42b92]/50 hover:bg-white/[0.03]'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                handleFilesAdded(e.target.files);
                e.target.value = '';
              }}
            />
            <Upload className="w-8 h-8 text-[#ff8ec5] mx-auto mb-2 opacity-80" />
            <div className="text-sm text-white font-medium">
              Click to select files, or drag and drop here
            </div>
            <p className="text-xs text-white/40 mt-1">Single or multiple files supported</p>
          </div>

          {/* Queue List */}
          {uploadQueue.length > 0 && (
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between text-xs text-white/60">
                <span>Selected Files ({uploadQueue.length})</span>
                <button
                  type="button"
                  onClick={() => setUploadQueue([])}
                  disabled={uploading}
                  className="text-red-400 hover:underline disabled:opacity-50"
                >
                  Clear all
                </button>
              </div>

              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {uploadQueue.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-lg border border-white/10 bg-white/[0.02] p-2.5 flex items-center justify-between gap-3 text-xs"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {getFileIcon('', item.file.name)}
                      <span className="text-white truncate max-w-xs">{item.file.name}</span>
                      <span className="text-white/40 font-mono text-[11px] shrink-0">
                        {formatBytes(item.file.size)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {item.status === 'queued' && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 text-[10px]">
                          Ready
                        </span>
                      )}
                      {item.status === 'uploading' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 text-[10px]">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Uploading…
                        </span>
                      )}
                      {item.status === 'success' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 text-[10px]">
                          <CheckCircle2 className="w-3 h-3" />
                          Uploaded
                        </span>
                      )}
                      {item.status === 'error' && (
                        <span
                          className="px-2 py-0.5 rounded-full bg-red-500/10 text-red-300 text-[10px] max-w-[180px] truncate"
                          title={item.error}
                        >
                          {item.error || 'Failed'}
                        </span>
                      )}

                      {item.status !== 'uploading' && (
                        <button
                          type="button"
                          onClick={() => removeQueueItem(item.id)}
                          className="text-white/40 hover:text-white p-1"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  disabled={
                    uploading ||
                    !uploadQueue.some(
                      (item) => item.status === 'queued' || (item.status === 'error' && !item.error?.includes('limit'))
                    )
                  }
                  onClick={handleUploadSubmit}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#d42b92] hover:bg-[#ff8ec5] text-white text-xs font-medium transition-colors disabled:opacity-40"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Uploading files…</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-3.5 h-3.5" />
                      <span>
                        Upload Selected (
                        {
                          uploadQueue.filter(
                            (i) => i.status === 'queued' || (i.status === 'error' && !i.error?.includes('limit'))
                          ).length
                        }
                        )
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload feedback banner if active filter conceals newly uploaded files */}
      {recentUploadNotice && recentUploadNotice.hiddenByFilters && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 flex flex-wrap items-center justify-between gap-2 text-xs text-amber-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
            <span>
              Upload complete! {recentUploadNotice.count} file(s) saved, but your current source or search
              filter may hide them.
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setSourceFilter('all');
              setSearchQuery('');
              setRecentUploadNotice(null);
            }}
            className="px-2.5 py-1 rounded-lg bg-amber-400/20 hover:bg-amber-400/30 text-amber-100 font-medium transition-colors"
          >
            Clear filters & view all
          </button>
        </div>
      )}

      {/* Search and Source Filter Pills */}
      <div className="space-y-2.5">
        <div className="relative">
          <Search className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents by filename, label, source, or stage…"
            className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-10 py-2.5 text-white text-sm focus:outline-none focus:border-[#d42b92]/60 placeholder:text-white/35"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Source Pills (for startup multi-source view) */}
        {!applicationId && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {SOURCE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setSourceFilter(opt.id)}
                className={`px-2.5 py-1 rounded-full text-xs transition-colors border ${
                  sourceFilter === opt.id
                    ? 'border-[#d42b92] bg-[#d42b92]/20 text-white font-medium shadow-sm shadow-[#d42b92]/20'
                    : 'border-white/10 text-white/50 hover:text-white/80 hover:border-white/20'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Bulk Startup Access Controls (Admin only) */}
      {!isMentor && startupId && filteredDocs.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-xl border border-[#2C2C34] bg-[#08080A] text-xs">
          <span className="text-[#8E8E99]">
            Startup Visibility Controls ({filteredDocs.filter((d) => d.sharedWithStartup).length} of {filteredDocs.length} visible):
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleBulkAccess(true)}
              className="px-2.5 py-1 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-xs font-semibold transition-colors flex items-center gap-1"
              title="Grant startup access to all filtered documents"
            >
              <CheckSquare className="w-3.5 h-3.5" />
              <span>Grant All to Startup</span>
            </button>
            <button
              type="button"
              onClick={() => handleBulkAccess(false)}
              className="px-2.5 py-1 rounded-lg border border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4] hover:bg-white/[0.04] text-xs font-medium transition-colors flex items-center gap-1"
              title="Revoke startup access for all filtered documents"
            >
              <EyeOff className="w-3.5 h-3.5" />
              <span>Revoke All</span>
            </button>
          </div>
        </div>
      )}

      {/* Document Items List */}
      <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
        {loading && (
          <div className="rounded-xl border border-white/10 p-8 text-center text-white/40 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-[#ff8ec5]" />
            Scanning startup documents repository…
          </div>
        )}

        {!loading && filteredDocs.length === 0 && (
          <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center space-y-3">
            <FolderOpen className="w-8 h-8 text-white/20 mx-auto" />
            <div className="text-white/60 text-sm font-medium">No documents found</div>
            <p className="text-xs text-white/35 max-w-sm mx-auto">
              {searchQuery || sourceFilter !== 'all'
                ? 'Try adjusting your search query or source filter.'
                : 'Documents uploaded across applications, company profile, onboarding, due diligence, and batch tasks will appear here.'}
            </p>
            {!isMentor && (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setShowUploadPanel(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#d42b92] hover:bg-[#ff8ec5] text-white text-xs font-medium transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" />
                  <span>Upload Documents</span>
                </button>
              </div>
            )}
          </div>
        )}

        {!loading &&
          filteredDocs.map((doc, idx) => {
            const displayName = doc.name || doc.label || doc.title || 'Untitled Document';
            const subLabel = doc.label && doc.label !== displayName ? doc.label : null;
            return (
              <div
                key={doc.id || doc.url || idx}
                className="rounded-xl border border-white/10 bg-white/[0.015] hover:border-white/20 hover:bg-white/[0.03] p-3.5 flex flex-wrap items-center justify-between gap-3 transition-colors group"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-black/40 border border-white/10 shrink-0 mt-0.5">
                    {getFileIcon(doc.url, displayName)}
                  </div>
                  <div className="min-w-0 space-y-1">
                    <div className="text-sm font-medium text-white truncate max-w-md">
                      {displayName}
                    </div>
                    {subLabel && (
                      <div className="text-xs text-white/40 truncate max-w-sm">{subLabel}</div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/40">
                      {sourceBadge(doc.source || doc.sourceLabel)}
                      {doc.stageTitle && <span>· {doc.stageTitle}</span>}
                      {doc.size > 0 && <span>· {formatBytes(doc.size)}</span>}
                      {doc.uploadedAt && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-white/30" />
                          {new Date(doc.uploadedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action: Open in new tab / download & Startup Access Toggle */}
                <div className="flex items-center gap-2 shrink-0 ml-auto">
                  {!isMentor && doc.docId && (
                    <button
                      type="button"
                      disabled={togglingDocId === (doc.id || doc.docId)}
                      onClick={() => handleToggleAccess(doc, !doc.sharedWithStartup)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        doc.sharedWithStartup
                          ? 'border border-emerald-500/30 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
                          : 'border border-[#2C2C34] bg-[#08080A] text-[#8E8E99] hover:text-[#F2F2F4] hover:border-[#D42B92]/40'
                      }`}
                      title={doc.sharedWithStartup ? 'Click to revoke startup access' : 'Click to grant startup access'}
                    >
                      {doc.sharedWithStartup ? (
                        <>
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                          <span>Visible to Startup</span>
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-3.5 h-3.5 text-[#8E8E99]" />
                          <span>Admin Only</span>
                        </>
                      )}
                    </button>
                  )}

                  {doc.url ? (
                    <a
                      href={resolveFileUrl(doc.url)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 hover:border-[#d42b92]/60 hover:bg-[#d42b92]/10 text-white text-xs font-medium transition-colors"
                    >
                      <span>View</span>
                      <ExternalLink className="w-3 h-3 text-[#ff8ec5]" />
                    </a>
                  ) : (
                    <span className="text-xs text-white/30">Pending Upload</span>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

export function SearchableDocumentsModal({ isOpen, onClose, startupId, applicationId, title }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0f0f12] border border-white/15 rounded-2xl w-full max-w-2xl p-6 shadow-2xl relative max-h-[90vh] overflow-hidden flex flex-col">
        <SearchableStartupDocuments
          startupId={startupId}
          applicationId={applicationId}
          onClose={onClose}
          title={title}
        />
      </div>
    </div>
  );
}

export default SearchableStartupDocuments;
